// 80 §1/§2 (78b) — THE CARD THAT MOUNTS THE MODAL.
//
// 78 built every route this needs and the modal itself, and left the mode
// `optional` PERMITTING A SHAPE NOBODY COULD TAKE UP: `secondStepFor` asks for a
// second step when the tenant requires one or when the person has one, so under
// `optional` nothing asks anybody to enrol and the only enrolment surface was
// the forced path (AUTH-7) that an `optional` tenant never reaches. This card is
// the surface a person reaches by going looking.
//
// ── IT WRITES NO NEW LOOK AND FEEL, AND NO SECOND RECOVERY-CODES SCREEN ─────
//
// [RULED — AUTH-1] one modal, one look and feel; sequencing across PRs is
// permitted, three look-and-feels are not. So all three verbs land in
// `AuthStepModal`:
//
//   enrol       mode="enrol", NO challenge — the ordinary path, authenticated
//               by the Bearer token `api` already attaches (src/api.ts:291-294).
//   remove      DELETE /api/auth-factors/:id on his own row.
//   re-issue    POST /recovery-codes/regenerate answers the ten ONCE, and they
//               are handed to the modal's EXISTING 'codes' step (80 §1: "It is
//               the same step; do not build a second one").
//
// ── WHY THIS PANEL IS NOT GRANTABLE ─────────────────────────────────────────
//
// Every other panel in the personal family sits behind a `settings.personal.*`
// permission an administrator may withhold. This one must not: withholding it
// would withhold a person's ability to secure his own account, and under
// `optional` that reinstates 78b exactly — the mode would again permit a shape
// nobody can take up, this time by grant rather than by omission. So it renders
// for every authenticated person, and `_SG.personal` is unconditional because of
// it (a header renders iff a panel inside it renders, and this one always does).
import { useState, useEffect } from "react";
import { useTranslation } from "../i18n";
import { FS } from "../lib/tokens";
import AuthStepModal from "./AuthStepModal";
import {
  listAuthFactors,
  deleteAuthFactor,
  regenerateRecoveryCodes,
} from "../api";
import type { AuthFactorRow, AuthFactorsResponse } from "../api";
import type { AuthConfigLike } from "../lib/authShapes";
// 83 §2 — the name AUTH-6 will not let lie.
import { passwordlessTextKeys, AUTH_SHAPES, shapeForMode } from "../lib/authShapes";
// 83 — the sign-in screen's own marks, on the rows that will ask for them.
import { FaceGlyph, FingerprintGlyph } from "./AuthGlyphs";
// 83 (glance) — the trusted browsers belong INSIDE this panel.
import TrustedDevicesList from "./TrustedDevicesCard";

/** The one thing this card asks of its host. */
type Toast = (message: string) => void;

/**
 * What a rejected api call carries. `api.ts` has already put the server's
 * translated sentence in `message` (the `srvErr.<code>` lookup), so this is the
 * whole of what is read from it — and reading it through a named shape keeps
 * the `any` ratchet at zero for this file.
 */
type ApiFailure = { message?: string };

/**
 * What a panel row's right-hand column does. A discriminated union so the row
 * that re-issues recovery codes cannot be asked which SHAPE it enrols.
 */
type PanelAction =
  | { kind: "enrol"; shape: string; held: boolean }
  | { kind: "recovery" }
  | null;

/** What the modal is mounted for, or null when it is closed. */
// 82 §4 — an enrolment now carries WHICH shape was pressed. Without it the
// modal would read `shapes[0]` and a tenant permitting both would be able to
// enrol only the first, forever — the passkey button would open the square
// code.
type Mounted =
  | { mode: "enrol"; shape: string }
  | { mode: "codes"; codes: string[]; replaced: number }
  | null;

/**
 * The rows a person may act on.
 *
 * `recovery_code` rows are filtered out because ten codes are ONE fact to a
 * reader and the response already carries that fact as a count. The `password`
 * row is filtered out because it is not a login shape at all — it carries the
 * credential's expiry (77 §4b) and `DELETE /:id` answers 422 for it, so
 * offering a Remove button beside it would be offering a refusal.
 */
const loginFactors = (rows: AuthFactorRow[] | undefined): AuthFactorRow[] =>
  (rows || []).filter((r) => r.type !== "recovery_code" && r.type !== "password");

export default function AuthFactorsCard({ toast }: { toast?: Toast }) {
  const { t } = useTranslation();
  const [data, setData] = useState<AuthFactorsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirmId, setConfirmId] = useState<number | null>(null);
  const [mounted, setMounted] = useState<Mounted>(null);
  const [cfg, setCfg] = useState<AuthConfigLike | null>(null);

  // THE SERVER'S OWN WORDS, ALREADY IN THIS PERSON'S LANGUAGE. `api.ts` runs
  // the `srvErr.<code>` lookup at the one seam where an envelope becomes a
  // string (brief 38 §3), so `e.message` is the translated sentence for
  // `auth.not_a_factor`, `auth.not_enrolled`, `generic.not_found` and the rest.
  // Re-keying them here would be a second opinion about what the code means,
  // which is the thing that helper exists to prevent — so this only supplies
  // the sentence for a failure that produced no envelope at all.
  //
  // It takes the SENTENCE and not the key, so every key stays a literal first
  // argument to `t()` at its call site. Passing the key would make each one a
  // computed site — the blind spot `i18n-keys-complete.js` names in its own
  // header — and a missing French row would stop being checkable.
  const say = (e: unknown, sentence: string) =>
    setErr(String((e as ApiFailure)?.message || "").trim() || sentence);

  async function reload() {
    setErr("");
    try {
      setData(await listAuthFactors());
    } catch (e) {
      say(e, t("auth.card.load_failed", { fallback: "Could not read your sign-in settings." }));
    } finally {
      setLoading(false);
    }
  }

  // Mount only, and the load is INLINE rather than a call to `reload` — a
  // function re-created every render cannot be a dependency, and writing the
  // exemption as a directive would be a suppression this file does not need.
  // Nothing it sets appears in the deps either
  // (scripts/lint/effect-sets-its-own-dep.js).
  useEffect(() => {
    let live = true;
    listAuthFactors()
      .then((d) => { if (live) setData(d); })
      .catch((e) => {
        if (live) say(e, t("auth.card.load_failed", { fallback: "Could not read your sign-in settings." }));
      })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, []);

  // The seller-fallback text the modal's "How does this work?" panel shows
  // depends on the installation tier, which only this endpoint answers.
  // Defaulting to null is safe: `installationTier(null)` is 'cloud'.
  useEffect(() => {
    let live = true;
    fetch("/api/auth/config", { credentials: "same-origin" })
      .then((r) => (r.ok ? r.json() : null))
      .then((c) => { if (live) setCfg(c || null); })
      .catch(() => { if (live) setCfg(null); });
    return () => { live = false; };
  }, []);

  const rows = loginFactors(data?.factors);
  const enrolled = data?.enrolled || [];
  const permitted = data?.permitted || [];
  // Only 'totp' has a modal branch today (src/lib/authShapes.ts). A tenant may
  // permit shapes this build cannot yet enrol, so the offer is the intersection
  // rather than whatever the server permits.
  const canEnrolTotp = permitted.includes("totp") && !enrolled.includes("totp");
  // A passkey is not "one per account" the way an authenticator app is — a
  // person may hold a laptop's and a phone's, and AUTH-5 says any enrolled
  // shape signs him in. So this offers as long as the tenant permits it.
  const canEnrolPasskey = permitted.includes("webauthn");
  // 83 §2 — SHAPE (c). Offered only where the tenant permits it, and it is the
  // same ceremony asking the authenticator for a DISCOVERABLE credential — a
  // passkey enrolled for shape (b) cannot answer a passwordless sign-in,
  // because `residentKey: 'discouraged'` means the authenticator need not be
  // able to produce it unprompted. So this is a distinct enrolment and not a
  // relabelling of the button above it.
  const canEnrolPasswordless = permitted.includes("passwordless");
  // AUTH-6 — WHAT IT IS CALLED depends on the tenant's mode, and under
  // `permissive` the word "passwordless" would be a lie because the password is
  // still live and still a way in. `passwordlessTextKeys` has no argument that
  // returns the strict name once a mode is given, so there is no branch here to
  // get wrong.
  const pwMode = cfg?.passwordlessMode === "permissive" ? "permissive" : "strict";
  const pwName = passwordlessTextKeys(pwMode)?.nameKey || "auth.shape.passwordless.name";

  const remove = async (id: number) => {
    setBusy(true);
    setErr("");
    try {
      await deleteAuthFactor(id);
      setConfirmId(null);
      await reload();
      toast && toast(t("auth.card.removed", { fallback: "Authenticator removed." }));
    } catch (e) {
      // 404 reads as NOT FOUND and never as "not permitted" — the standing
      // ruling, and rendering it as a refusal would reinstate across the wall
      // exactly what the 404 exists to hide. `srvErr.generic.not_found` is that
      // sentence, so nothing here has to decide it.
      say(e, t("auth.card.remove_failed", { fallback: "It could not be removed." }));
    } finally {
      setBusy(false);
    }
  };

  const regenerate = async () => {
    setBusy(true);
    setErr("");
    try {
      const res = await regenerateRecoveryCodes();
      // Straight into the modal's own 'codes' step — the server answers these
      // once and there is nowhere to fetch them again.
      // `replaced` travels with them: re-issuing from here ALWAYS kills a
      // previous set, and the modal says so rather than the person finding out
      // when the printed sheet fails.
      setMounted({ mode: "codes", codes: res.recoveryCodes || [], replaced: res.replaced || 0 });
    } catch (e) {
      say(e, t("auth.card.regen_failed", { fallback: "New codes could not be issued." }));
    } finally {
      setBusy(false);
    }
  };

  // ── 83 (glance) — THE PANEL: SIX ROWS, THREE COLUMNS ──────────────────────
  //
  // `[RULED — Laurent, 2026-08-15, on the glance]`: "three columns, six rows.
  // Six titles — four modes, trusted browsers, recovery codes — six explanations
  // beneath them from the text that already exists in authShapes.ts, six actions
  // on the right. ROWS, NOT PILLS."
  //
  // AND THE EXPLANATIONS BELONG AT THE CHOICE, NOT BEHIND IT: "today you pick a
  // mode and then learn what it was." So each row carries its own sentence,
  // BEFORE the button that acts on it, and the modal's "How does this work?"
  // panel stops being the only place those words exist. The text is the SAME
  // `howKey` AUTH-11 already requires per shape — nothing new is written for
  // this screen, which is what keeps one shape from having two descriptions.
  const shapeRow = (id: string) => {
    const base = AUTH_SHAPES.find((x) => x.id === id);
    if (!base) return null;
    // AUTH-6 — shape (c) reads differently per tenant mode, and `shapeForMode`
    // is the only way to get at those texts.
    return shapeForMode(base, pwMode);
  };
  const factorOf = (type: string) => loginFactors(data?.factors).find((r) => r.type === type);
  // 83 (glance) — ROWS ARE MODES, CREDENTIALS ARE INSTANCES. A person is about to
  // hold three — a Mac, a Samsung and a USB key — and the redesign showed the
  // modes while LOSING the credentials: a passkey row said "in use" and named no
  // device, so a registered Samsung appeared nowhere. The old card listed them
  // and that was right. `.filter`, not `.find`: a boolean can only ever produce
  // a label, which is all that landed the first time this was attempted.
  const factorsOf = (type: string) => loginFactors(data?.factors).filter((r) => r.type === type);
  // 83 (glance) — HOW MANY PASSKEYS PREDATE THE CLASS COLUMN. Those are
  // `discoverable: null`, and `webauthnCeremony.excludeFor` excludes them from
  // BOTH classes deliberately: on a modern platform authenticator
  // `residentKey: 'discouraged'` often yields a resident credential anyway, so
  // assuming they are non-discoverable could let a new enrolment SILENTLY
  // REPLACE one the person relies on. The cost is that those devices refuse a
  // discoverable enrolment, and row 4 says so rather than letting somebody meet
  // it as a dead end.
  const unclassedPasskeys = factorsOf("webauthn").filter(
    (f) => (f as { discoverable?: boolean | null }).discoverable === null
      || (f as { discoverable?: boolean | null }).discoverable === undefined,
  ).length;

  const ROWS = [
    // The floor's no-second-step position. Not a shape (77 §4) and nothing to
    // set up — it is what you have when you have nothing else, and saying so is
    // the point: a person who finds no second step should know that IS a state.
    {
      id: "password",
      title: t("auth.panel.password.title", { fallback: "Password only" }),
      how: t("auth.panel.password.how", {
        fallback:
          "You sign in with your email address and password, and nothing else is asked for. Anyone who learns your password can sign in as you — from anywhere, without your phone and without your laptop. This is for demo purposes only, while the app contains no real data.",
      }),
      glyphs: false,
      inUse: enrolled.length === 0,
      action: null as PanelAction,
    },
    ...["totp", "webauthn", "passwordless"].map((id) => {
      const held = !!factorOf(id === "passwordless" ? "webauthn" : id);
      const permittedHere = permitted.includes(id);
      return {
        id,
        // 83's glance — LAURENT'S COPY, VERBATIM, and it supersedes the generic
        // `nameKey`/`howKey` on this screen. AUTH-11's four texts still exist and
        // are still linted and still shown in the modal's About panel; what this
        // panel needed was shorter prose written for a person CHOOSING between
        // six rows rather than reading about one shape. Same shape, two audiences.
        title: t(`auth.panel.${id}.title`, { fallback: id }),
        // 83 (glance) — row 4's fallback is spelled out rather than left empty,
        // because its last sentence is the one that saves twenty minutes: a
        // passkey made by the shape-(b) button is usually discoverable anyway,
        // so the door may already be open. Nothing else on this screen can say
        // that, and it was sitting in a commit message instead.
        how: t(`auth.panel.${id}.how`, {
          fallback: id === "passwordless"
            ? "You type nothing at all — no address, no password. Your device asks you to confirm with your fingerprint, your face or your security key, and you are in. This needs a passkey your device can find on its own."
            : "",
        })
          // 83 (glance) — "try the sign-in screen first" belongs to somebody who
          // ALREADY HAS AN ACCOUNT, so it is appended HERE and not carried in the
          // shared text: on the invitation screen a new joiner has no sign-in to
          // try and the advice would send him at a door that must refuse him.
          // One short text per shape, shared; the audience-specific sentence
          // appended where its audience is.
          + (id === "passwordless"
            ? " " + t("auth.panel.passwordless.already_have", {
              fallback:
                "Most phones and laptops make one that way regardless of which button created it, so try the passkey button on the sign-in screen first: if it lets you in, you already have what this needs and there is nothing to set up here.",
            })
            : "")
          + (id === "passwordless" && unclassedPasskeys > 0
            ? " " + t("auth.panel.passwordless.older_keys", {
              fallback:
                "A device that already holds one of your existing passkeys may refuse this — those were set up before the two kinds were told apart. Use a device that has none, or remove the existing one from the row above first.",
            })
            : ""),
        // 83's glance — the passkey rows carry the sign-in screen's own marks.
        glyphs: id !== "totp",
        inUse: id === "passwordless" ? false : held,
        action: !permittedHere
          ? null
          : ({ kind: "enrol", shape: id, held } as PanelAction),
      };
    }),
    {
      id: "trusted",
      title: t("settings.panel.trustedDevices", { fallback: "Trusted browsers" }),
      how: t("auth.panel.trusted.how", {
        fallback:
          "If you allow so AT LOGIN, for a period of 30 days, your trusted browser will not require authentification. Never allow this on a computer to which other people have access.",
      })
        // ── 83's glance, item 1: SAID ON THE ROW RATHER THAN DISCOVERED ──────
        //
        // [MEASURED] `/auth/passwordless/finish` never calls
        // `rememberThisBrowser` — it cannot, because what a trusted browser buys
        // is SKIPPING THE SECOND STEP and shape (c) has none. That is coherent
        // rather than a defect, and the second step's own passkey button DOES
        // honour the tick (`secondStep`'s webauthn arm mints the trust, and
        // `AuthStepModal.tsx:442` sends it) — so the distinction is between the
        // two WAYS IN, not between the two credentials.
        //
        // A person who signs in with a passkey alone would otherwise tick
        // nothing, see nothing, and be left to work out why.
        + " " + t("auth.panel.trusted.not_passwordless", {
          fallback:
            "It does not apply when you sign in with a passkey only: there is no second step to skip, so nothing is offered at that login.",
        }),
      glyphs: false,
      inUse: false,
      action: null as PanelAction,
    },
    {
      id: "recovery",
      title: t("auth.panel.recovery.title", { fallback: "Recovery codes" }),
      how: t("auth.panel.recovery.how", {
        fallback:
          "Safety if you lose your phone or your passkey: ten single-use codes, shown once. Print them and keep them somewhere other than the device they rescue. Provide one instead of the six digits — your password is still required, so the sheet alone opens nothing.",
      })
        // ── AND THE SENTENCE ABOVE IS NOT TRUE FOR EVERYONE ──────────────────
        //
        // [MEASURED] a recovery code is spent ONLY in `secondStep()`
        // (routes/auth.js:734), against a challenge minted ONLY by `login()`
        // after bcrypt verifies a password (`:207-208`). For somebody whose
        // password was cleared by a STRICT shape-(c) enrolment there is NO DOOR
        // to type a code at — the sheet opens nothing, and the way back is an
        // administrator clearing the passkey (AUTH-9(ii)).
        //
        // So that person is told, rather than left holding a printed page he
        // believes is a safety net. Appended rather than replacing, because the
        // first sentences are still true of the codes themselves.
        + (data && data.hasPassword === false
          ? " " + t("auth.panel.recovery.no_password", {
            fallback:
              "You have no password on this account, so there is nowhere to type a code: the sheet cannot get you back in. If you lose your passkey, an administrator of your firm must clear it so you can set up a new one.",
          })
          : ""),
      glyphs: false,
      inUse: false,
      action: enrolled.length > 0 ? ({ kind: "recovery" } as PanelAction) : null,
    },
  ];

  return (
    <>
      <p style={{ fontSize: FS.s11_5, color: "var(--ts)", margin: "6px 0 10px", lineHeight: 1.6 }}>
        {t("auth.card.lede", {
          fallback:
            "A second step asks for a code from your phone after your password, so knowing your password is not enough to reach your firm's files.",
        })}
      </p>

      {/* §2 — WHAT THE MODE MEANS, IN THE MODE'S OWN WORDS. */}
      {data && (
        <p style={{ fontSize: FS.s11, color: "var(--tm)", margin: "0 0 12px", lineHeight: 1.5 }}>
          {data.mode === "required"
            ? t("auth.card.mode_required", {
                fallback: "Your firm requires a second step for everyone.",
              })
            : data.mode === "off"
              ? t("auth.card.mode_off", {
                  fallback: "Your firm has turned the second step off, so there is nothing to set up here.",
                })
              : t("auth.card.mode_optional", {
                  fallback:
                    "Your firm leaves this to you. Nobody is prompted to set one up — it happens only when a person comes here and does it, as you are doing now.",
                })}
        </p>
      )}

      {err && (
        <div style={{ fontSize: FS.s11, color: "var(--dg, #e5484d)", margin: "0 0 8px" }}>{err}</div>
      )}

      {loading ? (
        <p style={{ fontSize: FS.s11, color: "var(--tm)" }}>
          {t("common.loading", { fallback: "Loading…" })}
        </p>
      ) : (
        <div style={{ display: "flex", flexDirection: "column" }}>
          {ROWS.map((row) => (
            <div key={row.id} style={S.row}>
              <div style={S.rowMain}>
                <div style={S.rowTitle}>
                  {/* 83's glance — the glyphs sit to the RIGHT of the title, so
                      every title starts at the same x and the column reads as a
                      list. Leading them indented two rows out of six. */}
                  <span>{row.title}</span>
                  {row.glyphs && (
                    <span style={S.rowGlyphs}>
                      <FaceGlyph />
                      <FingerprintGlyph />
                    </span>
                  )}
                  {row.inUse && (
                    <span style={S.inUse}>
                      {t("auth.panel.in_use", { fallback: "in use" })}
                    </span>
                  )}
                </div>
                <p style={S.rowHow}>{row.how}</p>
                {/* 83's glance — the trusted browsers live INSIDE this panel,
                    beside everything else about signing in, rather than
                    floating above PERSONAL PREFERENCES on their own. */}
                {row.id === "trusted" && <TrustedDevicesList toast={toast} />}

                {/* THE CREDENTIALS THEMSELVES, nested under their mode: the name
                    the person gave at enrolment, when it was added, and its OWN
                    Remove — "remove my passkey" is meaningless once there are
                    three of them. */}
                {(row.id === "totp" || row.id === "webauthn") && factorsOf(row.id).length > 0 && (
                  <div style={S.instances}>
                    {factorsOf(row.id).map((f) => (
                      <div key={f.id} style={S.instance}>
                        <span style={{ flex: 1, minWidth: 0 }}>
                          <span style={{ fontWeight: 500 }}>
                            {f.label || t("auth.panel.unnamed", { fallback: "Unnamed" })}
                          </span>
                          <span style={{ color: "var(--tm)", marginLeft: 8 }}>
                            {t("auth.panel.added", {
                              when: f.createdAt
                                ? new Date(f.createdAt).toLocaleDateString(undefined, { dateStyle: "medium" })
                                : "—",
                              fallback: `added ${f.createdAt ? new Date(f.createdAt).toLocaleDateString() : "—"}`,
                            })}
                          </span>
                        </span>
                        {confirmId === f.id ? (
                          <>
                            <button type="button" className="btn btn-danger" disabled={busy} onClick={() => remove(f.id)}>
                              {t("common.confirm", { fallback: "Confirm" })}
                            </button>
                            <button type="button" className="btn btn-ghost" disabled={busy} onClick={() => setConfirmId(null)}>
                              {t("common.cancel", { fallback: "Cancel" })}
                            </button>
                          </>
                        ) : (
                          <button type="button" className="btn btn-ghost" disabled={busy} onClick={() => setConfirmId(f.id)}>
                            {t("auth.card.remove", { fallback: "Remove" })}
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div style={S.rowAction}>
                {/* The row's action ADDS. Removal belongs to the INSTANCE, not
                    to the mode — you remove the Samsung, not "the passkey". */}
                {row.action && row.action.kind === "enrol" && (
                  <button type="button" className="btn btn-primary" disabled={busy}
                    onClick={() => setMounted({ mode: "enrol", shape: (row.action as { shape: string }).shape })}>
                    {row.action.held && row.id !== "passwordless"
                      ? t("auth.panel.add_another", { fallback: "Add another" })
                      : t("auth.panel.set_up", { fallback: "Set up" })}
                  </button>
                )}
                {row.action && row.action.kind === "recovery" && (
                  <button type="button" className="btn btn-ghost" disabled={busy} onClick={regenerate}>
                    {t("auth.card.reissue", { fallback: "Issue new codes" })}
                  </button>
                )}
                {/* A row a firm does not permit says so where its button would
                    have been, rather than leaving a person to wonder whether
                    the screen is broken. */}
                {!row.action && row.id !== "password" && row.id !== "trusted" && (
                  <span style={S.notOffered}>
                    {row.id === "recovery"
                      ? t("auth.panel.recovery_none", { fallback: "after your first setup" })
                      : t("auth.panel.not_offered", { fallback: "not offered by your firm" })}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {mounted && (
        <AuthStepModal
          mode={mounted.mode}
          // The shape the button named, not the first the tenant permits.
          shapes={mounted.mode === "enrol" ? [mounted.shape] : permitted}
          recoveryRemaining={data?.recoveryCodes || null}
          cfg={cfg}
          initialCodes={mounted.mode === "codes" ? mounted.codes : null}
          initialReplaced={mounted.mode === "codes" ? mounted.replaced : 0}
          onDone={() => {
            setMounted(null);
            reload();
          }}
          onCancel={() => {
            // An abandoned enrolment leaves NOTHING behind — /totp/start writes
            // no row (routes/authFactors.js), so there is nothing to undo.
            setMounted(null);
            reload();
          }}
        />
      )}
    </>
  );
}

// 83 (glance) — ROWS, NOT PILLS. Three columns per row: the title with its
// explanation beneath it, and the action on the right. A grid rather than a
// table because the middle column is empty on four of the six rows and a table
// would draw a cell there.
const S = {
  row: {
    display: "flex",
    alignItems: "flex-start",
    gap: 12,
    padding: "10px 0",
    borderBottom: "1px solid var(--br)",
  } as const,
  rowMain: { flex: 1, minWidth: 0 } as const,
  rowTitle: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    fontSize: FS.s12,
    fontWeight: 600,
    color: "var(--tp)",
  } as const,
  rowGlyphs: { display: "inline-flex", gap: 3, color: "var(--ac)" } as const,
  rowHow: {
    fontSize: FS.s11,
    color: "var(--tm)",
    margin: "4px 0 0",
    lineHeight: 1.55,
  } as const,
  rowAction: { display: "flex", alignItems: "center", gap: 6, flexShrink: 0 } as const,
  // "in use" and not a tick: a tick is a verdict and this is a statement of
  // fact about which way in is live for this person right now.
  inUse: {
    fontSize: FS.s10,
    color: "var(--ac)",
    border: "1px solid var(--ac)",
    borderRadius: 3,
    padding: "0 4px",
    fontWeight: 500,
  } as const,
  notOffered: { fontSize: FS.s11, color: "var(--tm)" } as const,
  // 83 (glance) — the instances of a mode, visibly inside it.
  instances: {
    marginTop: 8,
    paddingLeft: 10,
    borderLeft: "2px solid var(--br)",
    display: "flex",
    flexDirection: "column",
    gap: 2,
  } as const,
  instance: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: FS.s11_5,
    padding: "3px 0",
  } as const,
};
